import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';

interface LmsEditorHeaderProps {
  /** Back-button click handler (passed verbatim from each page). */
  onBack: () => void;
  /** Page title. May be a string or a conditional expression. */
  title: ReactNode;
  /** Subtitle / description shown beneath the title. */
  subtitle: ReactNode;
  /** Optional right-aligned action slot (e.g. FormActionButtons). */
  actions?: ReactNode;
}

/**
 * Shared header for the LMS editor pages (content editor, journey builder,
 * session form, module assignments). Renders a back button, a title/subtitle
 * block, and an optional right-aligned actions slot. When `actions` is omitted
 * the layout is unchanged — the `justify-between` wrapper simply holds the left
 * group, identical to the previous inline markup.
 */
export default function LmsEditorHeader({
  onBack,
  title,
  subtitle,
  actions,
}: LmsEditorHeaderProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-500 mt-1">{subtitle}</p>
        </div>
      </div>
      {actions}
    </div>
  );
}
